import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useIsMobile } from '@/hooks/use-mobile';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, UserPlus, Camera, Edit, Trash2, UserMinus, Loader2, ChevronDown, FileText, Users, MapPin, CheckCircle, Building2, UserCheck, Mail, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DialogFooter } from '@/components/ui/dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from '@/hooks/use-toast';
import { StudentAvatarModal } from '@/components/StudentAvatarModal';
import { ContactStudentModal } from './ContactStudentModal';
import { Separator } from '@radix-ui/react-separator';

interface Student {
  id: string;
  firstname: string;
  lastname: string;
  email?: string;
  phone?: string;
  mobile?: string;
  schools?: any[];
  mother_email?: string;
  parent_name_1?: string;
  parent_name_2?: string;
  mother_phone?: string;
  father_email?: string;
  father_phone?: string;
  invoice_split_between_parents?: boolean;
  invoice_split_mode?: string;
  parent_1_notify?: number | string | boolean;
  parent_2_notify?: number | string | boolean;
  profile_picture?: string;
  has_user_account?: boolean;
  username?: string;
  families?: any[];
  invitation_date?: string;
  invitation_code?: string;
  street?: string;
  street_number?: string;
  zip_code?: string;
  place?: string;
  note?: string;
}

interface EditStudentFormProps {
  student: Student;
  onSubmit: (formData: FormData, isInvite?: boolean) => Promise<void>;
  onCancel: () => void;
  sendInvite: (studentId: string) => Promise<void>;
  sendAccountInformation: (studentId: string) => Promise<void>;
  submitDelete: (studentId: string, familyId: number) => void;
  onStudentRefresh?: () => void;
  onOpenContact: (student: Student) => void;
}

const EditStudentForm: React.FC<EditStudentFormProps> = ({ student, onSubmit, onCancel, sendInvite, sendAccountInformation, submitDelete, onStudentRefresh, onOpenContact }) => {
  const [formData, setFormData] = useState<Student>({ ...student });
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>(student.profile_picture);
  const [invitationSent, setInvitationSent] = useState<string | null>(null);
  const [isInvite, setIsInvite] = useState(false);
  const isMobile = useIsMobile();
  const { t, locale } = useLanguage();
  const tabs = [
    { key: 'personal', label: t('students.personalInfo'), Icon: User },
    { key: 'billing', label: t('students.billingAddress'), Icon: MapPin },
    { key: 'parents', label: t('students.parentInfo'), Icon: Users },
    { key: 'invoice', label: t('students.invoiceSettings'), Icon: FileText },
  ];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [usernameSentTime, setUsernameSentTime] = useState<string | null>(null);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isSendingUsername, setIsSendingUsername] = useState(false);
  const [activeTab, setActiveTab] = useState<'personal' | 'billing' | 'parents' | 'invoice'>('personal');

 useEffect(() => {
  const canEnableInvoiceSplit = Boolean(
    student.parent_name_1 &&
    student.parent_name_2 &&
    student.mother_email &&
    student.father_email
  );

  setFormData(prev => ({
    ...prev,
    invitation_date: student.invitation_date,
    invitation_code: student.invitation_code,
    has_user_account: student.has_user_account,
    username: student.username,
    families: student.families,
    schools: student.schools,
    profile_picture: student.profile_picture,
    invoice_split_between_parents: canEnableInvoiceSplit && Boolean(student.invoice_split_between_parents),
    invoice_split_mode: canEnableInvoiceSplit && student.invoice_split_mode ? student.invoice_split_mode : '0',
    parent_1_notify: Number(student.parent_1_notify) === 1 ? 1 : 0,
    parent_2_notify: Number(student.parent_1_notify) === 1 ? 0 : (Number(student.parent_2_notify) === 1 ? 1 : 0),
  }));
}, [student]);


  const invitation_date = formData.invitation_date ? new Date(formData.invitation_date) : null;
  const invitation_code = formData.invitation_code || null;

  const { toast } = useToast();
  const canEnableInvoiceSplit = (data: Student) => Boolean(
    data.parent_name_1 &&
    data.parent_name_2 &&
    data.mother_email &&
    data.father_email
  );

 const handleInviteClick = async () => {
  if (!formData.email) {
    return toast({
      title: "INFORMATION",
      description: t('students.noEmailForInvite'),
      variant: "destructive",
    });
  }

  try {
    await sendInvite(formData.id);

    const now = new Date();
    const options = locale === 'fr' ? { hour: '2-digit', minute: '2-digit' } : { hour: 'numeric', minute: 'numeric', hour12: true };
    const time = now.toLocaleTimeString(locale);
    setInvitationSent(`${t('common.sendAt')} ${time}`);
  } catch (error) {
    console.error("Error sending invite:", error);
  }
};


  const handleSendUsernameClick = async () => {
    setIsSendingUsername(true);
    try {
      await sendAccountInformation(formData.id);
      const now = new Date();
      const options = locale === 'fr' ? { hour: '2-digit', minute: '2-digit' } : { hour: 'numeric', minute: 'numeric', hour12: true };
      const time = now.toLocaleTimeString(locale);
      setUsernameSentTime(`${t('common.sendAt')} ${time}`);
      setIsDropdownOpen(false);
    } catch (error) {
      console.error("Error sending account information:", error);
    } finally {
      setIsSendingUsername(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleAvatarClick = () => {
    setIsAvatarModalOpen(true);
  };

  const handleDeleteFamily = async (familyId: any) => {
    if (formData) {
      try {
        await submitDelete(formData.id, familyId);
        const updatedFamilies = formData.families.filter(family => family.id !== familyId);
        setFormData(prev => ({
          ...prev,
          families: updatedFamilies
        }));
      } catch (error) {
        console.error('Error deleting family:', error);
      }
    }
  };

  const handleAvatarUpdate = (newAvatarUrl?: string | null) => {
    const value = newAvatarUrl && newAvatarUrl.trim() !== '' ? newAvatarUrl : null;

    setAvatarPreview(value);
    setFormData(prev => ({
      ...prev,
      profile_picture: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formDataToSend = new FormData();
    formDataToSend.append('id', formData.id);
    formDataToSend.append('firstname', formData.firstname || '');
    formDataToSend.append('lastname', formData.lastname || '');
    formDataToSend.append('email', formData.email || '');
    formDataToSend.append('phone', formData.phone || '');
    formDataToSend.append('mobile', formData.mobile || '');
    formDataToSend.append('mother_email', formData.mother_email || '');
    formDataToSend.append('mother_phone', formData.mother_phone || '');
    formDataToSend.append('father_email', formData.father_email || '');
    formDataToSend.append('father_phone', formData.father_phone || '');
    formDataToSend.append('parent_name_1', formData.parent_name_1 || '');
    formDataToSend.append('parent_name_2', formData.parent_name_2 || '');
    const notify1 = Number(formData.parent_1_notify) === 1 && Boolean(formData.parent_name_1 && formData.mother_email);
    const notify2 = !notify1 && Number(formData.parent_2_notify) === 1 && Boolean(formData.parent_name_2 && formData.father_email);
    const notifyActive = notify1 || notify2;
    const invoiceSplitValid = !notifyActive && canEnableInvoiceSplit(formData) && Boolean(formData.invoice_split_between_parents);
    formDataToSend.append('parent_1_notify', notify1 ? '1' : '0');
    formDataToSend.append('parent_2_notify', notify2 ? '1' : '0');
    formDataToSend.append('invoice_split_between_parents', invoiceSplitValid ? '1' : '');
    formDataToSend.append('invoice_split_mode', invoiceSplitValid ? (formData.invoice_split_mode || 'equal') : '0');
    formDataToSend.append('street', formData.street || '');
    formDataToSend.append('street_number', formData.street_number || '');
    formDataToSend.append('zip_code', formData.zip_code || '');
    formDataToSend.append('place', formData.place || '');
    formDataToSend.append('note', formData.note || '');
    formDataToSend.append('isInvite', String(isInvite));

    if (invoiceSplitValid === false && !notifyActive && formData.invoice_split_between_parents && (!formData.parent_name_1 || !formData.parent_name_2 || !formData.mother_email || !formData.father_email)) {
      toast({
        title: t('students.invoiceSplitParentInfoRequiredTitle'),
        description: t('students.invoiceSplitParentInfoRequiredDescription'),
        variant: 'destructive',
      });
      setIsSubmitting(false);
      return;
    }

    try {
      await onSubmit(formDataToSend, isInvite);
      if (isInvite) {
        if (onStudentRefresh) {
          onStudentRefresh();
        }
        setIsInvite(false);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBadgeContent = () => {
    if (formData.has_user_account) {
      return (
        <>
          <span>{t('students.registered')}</span>
          <ChevronDown
            className="h-4 w-4 ml-2 cursor-pointer"
            onClick={toggleDropdown}
          />
        </>
      );
    } else {
      return <span>{t('students.noAccount')}</span>;
    }
  };

  return (
   <form onSubmit={handleSubmit} className={`${!isMobile ? 'h-auto' : 'h-full'} flex flex-col bg-white`}>
      {/* Header Profile Section - START */}
      <div className={`relative ${!isMobile ? '' : ''} flex items-center justify-between px-2 py-2 text-gray-500 border-b`}>
        <div className="flex items-center space-x-4">
          {/* Avatar Section */}
          <div className="relative">
            <Avatar
              key={avatarPreview || 'fallback'}
              className="h-20 w-20 border-4 border-white shadow-lg cursor-pointer"
              onClick={handleAvatarClick}
            >
              {avatarPreview ? (
                <AvatarImage src={avatarPreview} alt={`${formData.firstname} ${formData.lastname}`} />
              ) : (
                <AvatarFallback className="bg-white text-blue-600 text-3xl font-semibold">
                  {formData.firstname?.[0]}{formData.lastname?.[0]}
                </AvatarFallback>
              )}
            </Avatar>
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-2 shadow-lg cursor-pointer" onClick={handleAvatarClick}>
              <Camera className="h-4 w-4 text-blue-600" />
            </div>
          </div>

          {/* Profile Info */}
          <div className="flex-1 text-gray-800 flex flex-col justify-center">
            <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-3 mb-2">
              <h2 className="text-md font-semibold">
                {formData.firstname || formData.lastname ? `${formData.firstname} ${formData.lastname}` : t('students.newStudent')}
              </h2>
            </div>

            {/* Account Status */}
            <div className="flex items-center space-x-4 text-sm">
              {!formData.has_user_account ? (
                <div className="flex items-center space-x-2">
                  <UserPlus size={16} />
                  <button type="button" onClick={handleInviteClick} className="underline hover:no-underline">
                    {t('students.sendInvite')}
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <User size={16} />
                  <span>{formData.username}</span>
                </div>
              )}
            </div>

            <div>
              {/* School Badges */}
              {formData.schools && formData.schools.length > 1 && formData.schools.map((school) => (
                <Badge key={school.id} variant="secondary" className="text-xs h-5 mr-1 mt-1">
                  {school.school_type === 'S' ? (
                    <Building2 className="w-2.5 h-2.5 mr-1" />
                  ) : (
                    <UserCheck className="w-2.5 h-2.5 mr-1" />
                  )}
                  {school.school_name}
                </Badge>
              ))}
            </div>

          </div>
        </div>

      </div>
      {/* Header Profile Section - END */}
      {/* Content */}
      <div className={`flex-grow overflow-auto ${!isMobile ? 'max-h-[50vh]' : 'p-3'} pt-4 p-6`}
      style={{
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE 10+
      }}>


      {/* Invitation Date */}
        {invitation_date !== null && !formData.has_user_account && (
          <div className="bg-gray-100 text-gray-800 text-sm mt-1 p-2 rounded mb-5">
            {t('teachers.invitationAt')}: {invitation_date.toLocaleDateString(locale)}
            <Separator orientation="vertical" className="mx-2 h-4 text-dark-gray" />
            {t('students.studentInvitationDesc')}
            <Separator orientation="vertical" className="mx-2 h-4 text-dark-gray" />
            {t('students.studentInvitationDesc2')}
               <Separator orientation="vertical" className="mx-2 h-4 text-dark-gray" />
            <span className='block text-lg'>
            <span className='text-sm'>code:</span> <span className='font-mono text-blue-600 text-lg bg-blue-50 px-2 py-1 rounded-full'>{invitation_code}</span>
            </span>
          </div>
        )}


        <div className='flex items-center mb-4 text-sm space-x-2 mt-2'>
          <Badge className="bg-blue-200 text-blue-800">
              {renderBadgeContent()}
          </Badge>

          <Badge 
            variant="outline" 
            className={`cursor-pointer ${!formData.email ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-100'}`}
            onClick={() => {
              if (!formData.email) return;
              onOpenContact(formData);
            }}
            title={!formData.email ? t('students.noEmailForContact') : t('students.contactStudent')}
          >
            <Mail className="mr-1 h-3 w-3" />
            {t('students.contactStudent')}
          </Badge>
        </div>

        {/* Status Messages */}
        {invitationSent && (
          <div className="bg-green-100 text-green-800 text-sm mb-3 p-2 bg-gray-100 rounded-md">{invitationSent}</div>
        )}
        {usernameSentTime && (
          <div className="bg-green-100 text-green-800 text-sm mb-3 p-2 bg-gray-100 rounded-md">{usernameSentTime}</div>
        )}


        {isDropdownOpen && formData.has_user_account && (
          <div className="bg-gray-100 rounded-md shadow-md p-2 border rounded-md shadow-md mb-5">
           <span className="text-sm text-gray-700 block">
             {t('students.resendStudentAccountInformation')} <span className="font-mono text-blue-600">{formData.username}</span>.
             {" "} {t('students.resendStudentAccountInformation2')}
           </span>
            <Button variant="outline" size="sm" className="mt-2" onClick={handleSendUsernameClick} disabled={isSendingUsername}>
              {isSendingUsername ? (
                <span className="flex items-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.sending')}
                </span>
              ) : (
                <span className="flex items-center">
                  <ArrowRight className="mr-2 h-4 w-4" />
                  {t('students.sendIdentifier')}
                </span>
              )}
            </Button>
          </div>
        )}
        
        {/* Family Members */}
        {formData.families && formData.families.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Users className="mr-2 h-5 w-5" />
              {t('students.familyMembers')}
            </h3>
            <div className="space-y-3">
              {formData.families.map((family) => (
                <div key={family.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center space-x-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-blue-100 text-blue-600 text-sm">
                        {family.firstname?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{family.firstname}</span>
                        <Badge variant={family.isUserAccount ? "default" : "secondary"} className="text-xs">
                          {family.isUserAccount ? t('students.registered') : t('students.noAccount')}
                        </Badge>
                      </div>
                      {family.email && <p className="text-sm text-gray-600">{family.email}</p>}
                    </div>
                  </div>
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => handleDeleteFamily(family.id)}
                          className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50"
                        >
                          <UserMinus size={18} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('students.dissociateStudent')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-6">
          <Select value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
            <SelectTrigger className="bg-blue-50">
              <SelectValue placeholder={t('students.selectSection')} />
            </SelectTrigger>
            <SelectContent>
              {tabs.map(({ key, label, Icon }) => (
                <SelectItem key={key} value={key} className="flex items-center gap-2 h-10">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0" />
                    <span>{label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {activeTab === 'personal' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <User className="mr-2 h-5 w-5" />
              {t('students.personalInfo')}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstname" className="text-sm font-medium text-gray-700">{t('students.firstName')}</Label>
                  <Input
                    id="firstname"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="lastname" className="text-sm font-medium text-gray-700">{t('students.lastName')}</Label>
                  <Input
                    id="lastname"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">{t('students.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is-invite"
                  checked={isInvite}
                  onCheckedChange={(checked) => setIsInvite(!!checked)}
                  disabled={!formData.email?.trim() || formData.has_user_account}
                />
                <Label htmlFor="is-invite" className="text-sm text-gray-700">{t('students.sendInvite')}</Label>
              </div>
              <div className="pt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700">{t('students.phone')}</Label>
                  <Input
                    id="phone"
                    type="text"
                    name="phone"
                    value={formData.phone || ''}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="mobile" className="text-sm font-medium text-gray-700">{t('students.mobile')}</Label>
                  <Input
                    id="mobile"
                    type="text"
                    name="mobile"
                    value={formData.mobile || ''}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className='pt-8'>
                <h4 className="text-base font-semibold text-gray-800 mb-3">{t('students.note')}</h4>
                <textarea
                  id="note"
                  name="note"
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 resize-none"
                  rows={4}
                  value={formData.note || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      note: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className='pt-2'>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <MapPin className="mr-2 h-5 w-5" />
              {t('students.billingAddress')}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="street" className="text-sm font-medium text-gray-700">{t('students.street')}</Label>
                  <Input
                    id="street"
                    name="street"
                    value={formData.street || ''}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="street_number" className="text-sm font-medium text-gray-700">{t('students.streetNumber')}</Label>
                  <Input
                    id="street_number"
                    name="street_number"
                    value={formData.street_number || ''}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="zip_code" className="text-sm font-medium text-gray-700">{t('students.zipCode')}</Label>
                  <Input
                    id="zip_code"
                    name="zip_code"
                    value={formData.zip_code || ''}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="place" className="text-sm font-medium text-gray-700">{t('students.place')}</Label>
                  <Input
                    id="place"
                    name="place"
                    value={formData.place || ''}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'parents' && (
          <div className='pt-2'>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Users className="mr-2 h-5 w-5" />
              {t('students.parentInfo')}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4 bg-gray-50 p-2 rounded-lg border">
                  <div>
                    <Label htmlFor="parent_name_1" className="text-sm font-medium text-gray-700">{t('students.motherName')}</Label>
                    <Input
                      id="parent_name_1"
                      name="parent_name_1"
                      type="text"
                      value={formData.parent_name_1 || ''}
                      onChange={handleChange}
                      required={formData.invoice_split_between_parents}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mother_email" className="text-sm font-medium text-gray-700">{t('students.motherEmail')}</Label>
                    <Input
                      id="mother_email"
                      name="mother_email"
                      type="email"
                      value={formData.mother_email || ''}
                      onChange={handleChange}
                      required={formData.invoice_split_between_parents}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mother_phone" className="text-sm font-medium text-gray-700">{t('students.motherPhone')}</Label>
                    <Input
                      id="mother_phone"
                      name="mother_phone"
                      type="number"
                      value={formData.mother_phone || ''}
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="space-y-4 bg-gray-50 p-2 rounded-lg border">
                  <div>
                    <Label htmlFor="parent_name_2" className="text-sm font-medium text-gray-700">{t('students.fatherName')}</Label>
                    <Input
                      id="parent_name_2"
                      name="parent_name_2"
                      type="text"
                      value={formData.parent_name_2 || ''}
                      onChange={handleChange}
                      required={formData.invoice_split_between_parents}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="father_email" className="text-sm font-medium text-gray-700">{t('students.fatherEmail')}</Label>
                    <Input
                      id="father_email"
                      name="father_email"
                      type="email"
                      value={formData.father_email || ''}
                      onChange={handleChange}
                      required={formData.invoice_split_between_parents}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="father_phone" className="text-sm font-medium text-gray-700">{t('students.fatherPhone')}</Label>
                    <Input
                      id="father_phone"
                      name="father_phone"
                      type="number"
                      value={formData.father_phone || ''}
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'invoice' && (
          <div className='pt-2'>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FileText className="mr-2 h-5 w-5" />
              {t('students.invoiceSettings')}
            </h3>
            <div className="space-y-4">
              {(() => {
                const fr = locale === 'fr';
                const notifyMode = Number(formData.parent_1_notify) === 1 || Number(formData.parent_2_notify) === 1;
                const splitMode = !notifyMode && Boolean(formData.invoice_split_between_parents);
                const parents = [
                  { key: 1, name: formData.parent_name_1, email: formData.mother_email },
                  { key: 2, name: formData.parent_name_2, email: formData.father_email },
                ].filter(p => p.name && p.email);
                const selectSplit = () => {
                  if (!canEnableInvoiceSplit(formData)) {
                    toast({
                      title: t('students.invoiceSplitParentInfoRequiredTitle'),
                      description: t('students.invoiceSplitParentInfoRequiredDescription'),
                      variant: 'destructive',
                    });
                    return;
                  }
                  setFormData(prev => ({ ...prev, invoice_split_between_parents: true, invoice_split_mode: 'equal', parent_1_notify: 0, parent_2_notify: 0 }));
                };
                const selectNotify = (key?: number) => {
                  const k = key ?? parents[0]?.key;
                  if (!k) {
                    toast({
                      title: fr ? 'Informations parent manquantes' : 'Missing parent information',
                      description: fr ? "Renseignez le nom et l'email d'au moins un parent." : 'Fill in the name and email of at least one parent.',
                      variant: 'destructive',
                    });
                    return;
                  }
                  setFormData(prev => ({ ...prev, invoice_split_between_parents: false, invoice_split_mode: '0', parent_1_notify: k === 1 ? 1 : 0, parent_2_notify: k === 2 ? 1 : 0 }));
                };
                const clearAll = () => setFormData(prev => ({ ...prev, invoice_split_between_parents: false, invoice_split_mode: '0', parent_1_notify: 0, parent_2_notify: 0 }));
                return (
                  <>
                    {[
                      {
                        active: splitMode,
                        onSelect: selectSplit,
                        title: t('students.splitInvoicesBetweenParents'),
                        desc: t('students.splitInvoicesBetweenParentsHelp'),
                      },
                      {
                        active: notifyMode,
                        onSelect: () => selectNotify(),
                        title: fr ? 'Facturer au nom d’un parent' : 'Invoice in a parent’s name',
                        desc: fr
                          ? "Le nom du parent choisi apparaîtra sur la facture à la place de celui de l'élève. L'élève reste le bénéficiaire des cours."
                          : "The selected parent's name will appear on the invoice instead of the student's. The student remains the one taking the lessons.",
                      },
                    ].map((opt, i) => (
                      <div
                        key={i}
                        className={`rounded-xl border p-4 transition-colors ${opt.active ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted/50'}`}
                      >
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="invoice_option"
                            className="mt-1 h-4 w-4 accent-primary"
                            checked={opt.active}
                            onChange={opt.onSelect}
                            onClick={() => { if (opt.active) clearAll(); }}
                          />
                          <div className="space-y-1">
                            <span className="text-sm font-semibold text-foreground block">{opt.title}</span>
                            <p className="text-sm text-muted-foreground">{opt.desc}</p>
                          </div>
                        </label>

                        {i === 0 && splitMode && (
                          <div className="mt-3 ml-7 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
                            {t('students.parentInfoRequiredForSplit')}
                          </div>
                        )}

                        {i === 1 && parents.length === 0 && (
                          <p className="mt-3 ml-7 text-xs text-muted-foreground italic">
                            {fr ? "Renseignez le nom et l'email d'au moins un parent dans l'onglet Parents pour activer cette option." : 'Fill in the name and email of at least one parent in the Parents tab to enable this option.'}
                          </p>
                        )}

                        {i === 1 && notifyMode && parents.length > 0 && (
                          <div className="mt-3 ml-7 space-y-2">
                            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              {fr ? 'Nom affiché sur la facture' : 'Name shown on the invoice'}
                            </p>
                            {parents.map(p => {
                              const checked = Number(p.key === 1 ? formData.parent_1_notify : formData.parent_2_notify) === 1;
                              return (
                                <label
                                  key={p.key}
                                  className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer text-sm transition-colors ${checked ? 'border-primary bg-background' : 'border-border bg-background/60 hover:bg-muted/50'}`}
                                >
                                  <input
                                    type="radio"
                                    name="invoice_notify_parent"
                                    className="h-4 w-4 accent-primary"
                                    checked={checked}
                                    onChange={() => selectNotify(p.key)}
                                  />
                                  <div className="flex flex-col">
                                    <span className="font-medium text-foreground">{p.name}</span>
                                    <span className="text-xs text-muted-foreground">{p.email}</span>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={`flex-shrink-0 ${isMobile ? 'p-4 border-t bg-gray-50 rounded-t-3xl pb-8' : 'pt-4'}`}>
        <div className={`flex ${isMobile ? 'justify-center' : 'justify-end'} space-x-3`}>
          <Button type="button" variant="outline" onClick={onCancel} className="px-6">
            {t('common.cancel')}
          </Button>
          <Button type="submit" className="bg-gradient-to-r from-blue-600 to-blue-400 hover:from-blue-500 hover:to-blue-600 text-white px-8 py-2 transition-all duration-200">
            {isSubmitting ? (
              <span className="flex items-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.processing')}
              </span>
            ) : (
              <span className="flex items-center">
              <CheckCircle className="mr-2 h-4 w-4" />
              { t('common.save')}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Student Avatar Modal */}
      <StudentAvatarModal
        isOpen={isAvatarModalOpen}
        onClose={() => setIsAvatarModalOpen(false)}
        studentId={formData.id}
        currentAvatarUrl={avatarPreview || ''}
        onAvatarUpdate={handleAvatarUpdate}
        onStudentRefresh={onStudentRefresh}
      />

    </form>
  );
};

export default EditStudentForm;
